import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, Switch, Typography, useTheme } from "@mui/material";
import { ReactNode, useContext, useState } from "react";

import { Skill, PlayerCharacterDisplay } from "../model/character";
import { StatDisplay } from "./StatDisplay";
import { CharacterContext } from "../model/characterContext";

import { ReactComponent as WorkshopIcon } from '../components/icons/raw/Icon_Workshop.svg'
import { ReactComponent as ToolsIcon } from '../components/icons/raw/Icon_Tools.svg'
import HexagonIcon from '@mui/icons-material/Hexagon';

import styled from 'styled-components';
import { Modal } from "./Modal";

const Hexagon = styled.div`
    width: 55px;
    height: 55px;
    margin: 0 auto;
    position: relative;
    cursor: pointer;
`

const HexagonNumber = styled.div`
    position: absolute;
    top:0;
    left:0;
    right:0;
    bottom:0;
    font-size: 20px;
    line-height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff
`;

const HexagonSide = styled.div`
    display: flex;
    position: absolute;
    left: 65px;
    top: 0px;
    bottom: 0px;
    width:15px;
    flex-direction: column;
    justify-content: center;
    &>*:not(:first-child) {
        margin-top: 5px;
    }
`

const SkillRowValue = styled.div`
    width: 50px;
`;

interface SkillFieldProps {
    value: Skill;
    character: PlayerCharacterDisplay;
    toolboxEnabled?: boolean;
    showExtended?: boolean;
    last?: boolean;
    onChange: (value: Skill) => void;
}

const SkillRow = (props: {name: ReactNode, value: ReactNode, control?: ReactNode }) => {
    return <Box><Grid container height="33px" spacing={1} paddingLeft={1}>
        <Grid item xs={8}>{props.name}</Grid>
        <Grid item xs={2}>{props.value}</Grid>
        <Grid item xs={2} textAlign="right" alignItems="center">{props.control}</Grid>
    </Grid></Box>
}

export const SkillField = (props: SkillFieldProps) => {
    const { value, toolboxEnabled, onChange, character } = props;
    const characterContext = useContext(CharacterContext)!;
    const theme = useTheme();

    const [ skillValue, setSkillValue ] = useState(value);
    const [ editing, setEditing ] = useState(false);

    const handleSubmit = () => {
        onChange(skillValue);
        setEditing(false);
    }

    const handleCancel = () => {
        setSkillValue(value);
        setEditing(false);
    }

    const bonusAttribute = character.attributes.find(a => a.id === value.attributeId);
    const bonusAdustmentCalculator = (value: number) => {
        if(value > 3) return 1;
        if ( value < 2) return -1;
        return 0;
    }
    const bonusAttributeAdjustment =  bonusAdustmentCalculator(bonusAttribute?.current ?? 2);
    const workshopBonus = skillValue.workshop ? 1 : 0;
    const toolboxBonus = toolboxEnabled ? (skillValue.toolbox ? 1 : 0) : 0
    const trainedValue = skillValue.trained ? 1: -2;
    const traitBonus = characterContext.getAdjustment(value.id);
    const total = trainedValue + toolboxBonus + workshopBonus + bonusAttributeAdjustment + traitBonus;

    return <>
        <Modal maxWidth="xs" open={editing} onClose={handleCancel} title={value.name} actions={[
            { label: "Cancel", onClick: handleCancel },
            { label: "Modify", onClick: handleSubmit, primary: true },
        ]}>
            <Stack spacing={1}>
                <SkillRow name="Trained:" value={StatDisplay(trainedValue)} control={<Switch size="small" checked={skillValue.trained} onChange={(v) => setSkillValue(current => { return { ...current, trained: v.target.checked}} )} />} />
                { toolboxEnabled !== false && <SkillRow name="Toolbox:" value={StatDisplay(toolboxBonus)} control={<Switch size="small" checked={skillValue.toolbox} onChange={(v) => setSkillValue(current => { return { ...current, toolbox: v.target.checked ?? false}} )} />} />}
                <SkillRow name="Workshop:" value={StatDisplay(workshopBonus)} control={<Switch size="small" checked={skillValue.workshop} onChange={(v) => setSkillValue(current => { return { ...current, workshop: v.target.checked}} )} />} />
                <SkillRow name={`Attribute: (${bonusAttribute?.name ?? "None"})`} value={StatDisplay(bonusAttributeAdjustment)} />
                <SkillRow name="Species/Trait:" value={StatDisplay(traitBonus)} />
                <SkillRow name={<strong>TOTAL:</strong>} value={StatDisplay(total)} />
            </Stack>
        </Modal>
        <Grid item xs={4} sm={3} onClick={() => setEditing(true)} marginBottom={2}>
            <Hexagon>
                <HexagonSide>
                    {value.toolbox && <ToolsIcon fill={theme.palette.primary.main} />}
                    {value.workshop && <WorkshopIcon fill={theme.palette.primary.main} />}
                </HexagonSide>
                <HexagonNumber>
                    <Box fontSize="55px"><HexagonIcon fontSize="inherit" color="primary" /></Box>
                </HexagonNumber>
                <HexagonNumber>
                    {StatDisplay(total)}
                </HexagonNumber>
            </Hexagon>
            <Typography variant="body1" textAlign={"center"}>{value.name}</Typography>
        </Grid>
    </>
}